<script setup lang="ts">
    import { computed } from 'vue';
    import { DateTime } from 'luxon';
    import { taggedLogger } from '@/util';
    import type { CaseNotification } from '@/api/caseSettings';
    import { caseCompletedMessage } from '@/stores/useCaseSyncStore';
    import { timeElapsed } from '@/util/dateTime';

    const props = defineProps<{
        value: CaseNotification;
    }>();

    const log = taggedLogger('notification-list-item');

    const icon = computed(() => {
        switch (props.value.type) {
            case 'Info':
            case 'Warning':
            case 'Error':
                return 'mdi-information-outline';
            default:
                return '';
        }
    });

    const iconColor = computed(() => {
        switch (props.value.type) {
            case 'Info':
                if (props.value.message === caseCompletedMessage) {
                    return 'green';
                }
                return 'info';
            case 'Warning':
            case 'Error':
                return 'error';
            default:
                return '';
        }
    });

    const createdOn = computed(() => {
        const timestamp = props.value.timestamp;
        const when = DateTime.fromISO(timestamp);
        if (when) {
            return when.toRelativeCalendar({ unit: timeElapsed(when) });
        } else {
            log.warn('Failed to parse timestamp from notification message');
        }
        return null;
    });
</script>

<template>
    <v-list-item lines="one" class="pa-0 pb-5 notification-menu-item">
        <v-layout>
            <v-icon
                class="mr-5 notification-icon"
                :class="value.type"
                size="25"
                :color="iconColor"
                style="opacity: 1"
            >
                {{ icon }}
            </v-icon>
            <div>
                <v-list-item-subtitle class="text-on-dark-blue opacity-100" style="display: block">
                    {{ value.message }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="value.timestamp" class="planner-grey">
                    {{ createdOn }}
                </v-list-item-subtitle>
            </div>
        </v-layout>
    </v-list-item>
</template>

<style scoped>
    .notification-icon.Error,
    .notification-icon.Warning {
        transform: rotateX(180deg);
    }

    .notification-menu-item:last-child {
        padding-bottom: 0 !important;
    }
</style>
